import { useMemo } from 'react';
import { useLocation } from 'react-router';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { useDirectoryViewMore } from '~/ui-rocco/pages/category-page/hooks/useProductViewMore';
import { useTilesAdminData } from '~/modules-rocco/admin/hooks/use-tiles-admin-data';
import {
    DirectoryUniformGrid,
    type DirectoryGridColumns,
    type DirectoryGridGapVariant,
} from '~/ui-rocco/pages/category-page/DirectoryUniformGrid';
import { useDirectoryGridInjections } from './use-directory-grid-injections';
import type { DirectoryEntityType } from './DirectoryBrowsePage';
import type { ContentGateState } from '~/modules-rocco/content-gate/content-gate';
import {
    IMAGE_LABEL_PAGE_SIZE,
    IMAGE_WALL_WIDTH,
} from '@modules-rocco/projects/config/project-browse-views';

/** Grid page size for professionals/projects (matches the browse loader). */
const DIRECTORY_PAGE_SIZE = 36;

/**
 * How a browse grid is laid out and filled. Fixed per look, never derived from
 * the column count or the active category, so a grid reads the same at every
 * width and depth.
 */
interface DirectoryGridPresentation {
    /** Columns below md, where the vertical's reading order decides. */
    mobileColumns: 1 | 2;
    gapVariant: DirectoryGridGapVariant;
    /** Skeleton tile aspect while a page loads; the grid's own default if unset. */
    skeletonAspectClass?: string;
    /** Tiles per deep-link page — the loader's page size for this grid. */
    pageSize: number;
    /** Fixed `sizes` for the tile media, where the column-aware default is wrong. */
    imageSizes?: string;
    /** Caption under each tile, or an image wall without one. */
    mediaOnly: boolean;
    /** Open each tile in a new tab, so the grid keeps its scroll position. */
    openInNewTab: boolean;
    /**
     * Page on scroll alone, with no View More button to press. An image wall
     * reads as one continuous wall, so stopping it behind a button breaks the
     * one thing the page is for.
     */
    autoLoadMore: boolean;
}

/**
 * The looks a browse grid comes in: one per vertical, plus the image wall the
 * Spaces / Styles views render. The wall is a look rather than a vertical —
 * it browses the project index like `projects` does, and differs only in how
 * it presents what it finds.
 */
export type DirectoryGridPreset = DirectoryEntityType | 'imageWall';

const CAPTIONED_GRID = {
    gapVariant: 'standard',
    pageSize: DIRECTORY_PAGE_SIZE,
    mediaOnly: false,
    openInNewTab: false,
    autoLoadMore: false,
} as const satisfies Partial<DirectoryGridPresentation>;

const GRID_PRESENTATION: Record<
    DirectoryGridPreset,
    DirectoryGridPresentation
> = {
    // Products keep 2-up on mobile (compare-and-shop); the imagery-led
    // verticals go single column so the photography leads.
    products: { ...CAPTIONED_GRID, mobileColumns: 2 },
    professionals: { ...CAPTIONED_GRID, mobileColumns: 1 },
    // Projects use the narrower 22px column gap and landscape tiles.
    projects: {
        ...CAPTIONED_GRID,
        mobileColumns: 1,
        gapVariant: 'projects',
        skeletonAspectClass: 'aspect-tile-project-browse',
    },
    // Uncaptioned tiles on a tight 8px gutter both ways. The tiles ask for the
    // candidate the three-column projects grid uses: bigger than they need, but
    // already in the cache from that page, and it is what the preview opens on.
    imageWall: {
        ...CAPTIONED_GRID,
        mobileColumns: 1,
        gapVariant: 'imageLabel',
        skeletonAspectClass: 'aspect-tile-image-label',
        pageSize: IMAGE_LABEL_PAGE_SIZE,
        imageSizes: IMAGE_WALL_WIDTH,
        mediaOnly: true,
        openInNewTab: true,
        autoLoadMore: true,
    },
};

interface DirectoryBrowseGridProps {
    entityType: DirectoryEntityType;
    /**
     * Desktop columns — the page default (4 professionals, 3 projects) or the
     * user's Display dropdown selection (2–6).
     */
    columns: DirectoryGridColumns;
    /** Plural noun for the end-of-list message ("professionals", "projects"). */
    entityNoun: string;
    /** Tile media aspect ratio to stamp on each tile (e.g. "portrait"). */
    aspectRatio: NonNullable<TileViewModel<TileableModel>['aspectRatio']>;
    /** Which look to render. Defaults to the one named after the vertical. */
    preset?: DirectoryGridPreset;
    /**
     * Intercept the tile click instead of following its link — the image walls
     * open a preview overlay. Receives the clicked tile's index along with the
     * tiles actually on screen: View More appends to that set and a removed
     * tile drops out of it, so an index alone would point at the wrong tile —
     * or past the end — the moment the grid grows past its first page.
     */
    onTileClick?: (index: number, tiles: TileableModel[]) => void;
    /**
     * What makes two tiles the same tile, when the model id is not it. The
     * image walls key on the photo: one photo can be indexed under several
     * ids, so pages far apart can each carry one of them.
     */
    dedupeKey?: (model: TileableModel) => string | number;
    /** Directory category path (e.g. "/professionals"). */
    categoryLink: string;
    /** First page of raw tile models from the loader. */
    initialTiles: TileableModel[];
    /** Total results across the catalogue. */
    totalCount: number;
    /** Active facet filters carried into View More fetches. */
    filters?: Record<string, unknown>;
    /** In-listing keyword term, carried into View More fetches with them. */
    search?: string;
    /** Active sort from the URL (Sort dropdown), carried into View More fetches. */
    sortBy?: string;
    /**
     * First grid page hydrated on the server (1-indexed). Pages above render as
     * lazy-loading skeletons. 1 unless the URL deep-linked via `?page=N`.
     */
    firstLoadedPage?: number;
    /** Page the URL asked for (1-indexed), scrolled to on mount. Default 1. */
    requestedPage?: number;
    /** Active category name for the end-of-list "…in {label}" prompt. */
    activeCategoryLabel?: string;
    /**
     * Projects currently pinned to the top of the directory — drives the
     * admin context menu's Pin/Unpin label on project tiles.
     */
    featuredProjectIds?: number[];
    /**
     * IDs mid Remove-From-Library fade-out — the tile stays mounted at
     * opacity-0 so the animation can play.
     */
    removedFromLibraryIds?: Set<number>;
    /**
     * IDs whose removal fade has finished — dropped from the grid so it
     * reflows and fills the empty cell.
     */
    unmountedFromLibraryIds?: Set<number>;
    /** Admin action: pin/unpin a project to the top of the directory. */
    onToggleFeature?: (projectId: number, feature: boolean) => void;
    /** Admin action: soft-hide a project/product from the directory. */
    onRemoveFromLibrary?: (
        itemType: 'project' | 'product',
        itemId: number,
        itemName: string,
    ) => void;
    /**
     * Content gate for this request. When closed the grid ends at the tiles the
     * loader allowed, blurred out under the sign-in panel.
     */
    contentGate?: ContentGateState;
}

/**
 * The professionals/projects browse grid: reuses the products View More
 * machinery (infinite scroll + deep-link skeletons + POP restore) and the
 * admin/follow tiles, rendered through the generalized DirectoryUniformGrid at
 * the entity's column count and tile aspect.
 */
export const DirectoryBrowseGrid = ({
    entityType,
    columns,
    entityNoun,
    aspectRatio,
    preset,
    onTileClick,
    dedupeKey,
    categoryLink,
    initialTiles,
    totalCount,
    filters,
    search,
    sortBy,
    firstLoadedPage = 1,
    requestedPage = 1,
    activeCategoryLabel,
    featuredProjectIds,
    removedFromLibraryIds,
    unmountedFromLibraryIds,
    onToggleFeature,
    onRemoveFromLibrary,
    contentGate,
}: DirectoryBrowseGridProps) => {
    const presentation = GRID_PRESENTATION[preset ?? entityType];
    const injections = useDirectoryGridInjections();

    // Wrap the loader's raw models as tile view models for the hook.
    const wrappedInitial = useMemo<TileViewModel<TileableModel>[]>(
        () => initialTiles.map((model) => ({ model })),
        [initialTiles],
    );

    const {
        products: viewMoreTiles,
        hasMore,
        isLoading,
        error,
        loadMore,
        loadSkeletonPage,
        loadingPages,
        currentFirstLoadedPage,
        nextPage,
    } = useDirectoryViewMore({
        entityType,
        pageSize: presentation.pageSize,
        initialProducts: wrappedInitial,
        totalCount,
        categoryLink,
        filters,
        search,
        sortBy,
        dedupeKey,
        firstLoadedPage,
        // The uniform grid keeps `?page=N` in step with the page actually in
        // view (useViewportPageSync) — the request-time rewrite would run
        // ahead of the reader under infinite scroll.
        updateUrlOnLoad: false,
        gated: contentGate?.gated ?? false,
    });

    // Crawlable `?page=N` anchor for the Load More control — mirrors the
    // deep-link URL a click loads, so bots can walk the pages without JS.
    const location = useLocation();
    const nextPageHref = useMemo(() => {
        const params = new URLSearchParams(location.search);
        params.set('page', String(nextPage));
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search, nextPage]);

    // Enrich with admin/follow data, then stamp the entity's tile aspect. The
    // media keeps the shared tile radius. Tiles whose Remove-From-Library fade
    // has finished are dropped so the grid reflows and fills the empty cell.
    const withAdmin = useTilesAdminData(viewMoreTiles);
    // The tiles actually rendered, in render order — handed to onTileClick so a
    // preview can page through what the reader can see.
    const loadedModels = useMemo(
        () =>
            withAdmin
                .filter(
                    (tile) =>
                        !(unmountedFromLibraryIds?.has(tile.model.id) ?? false),
                )
                .map((tile) => tile.model),
        [withAdmin, unmountedFromLibraryIds],
    );
    const tiles = useMemo(
        () =>
            withAdmin
                .filter(
                    (tile) =>
                        !(unmountedFromLibraryIds?.has(tile.model.id) ?? false),
                )
                .map((tile, index) => ({
                    ...tile,
                    aspectRatio,
                    mediaOnly: presentation.mediaOnly,
                    newTab: presentation.openInNewTab,
                    // With a click handler the tile is a preview trigger, not a
                    // link: disableLink stops the navigation the href would do.
                    ...(onTileClick
                        ? {
                              disableLink: true,
                              onClick: () => onTileClick(index, loadedModels),
                          }
                        : {}),
                })),
        [
            withAdmin,
            aspectRatio,
            presentation,
            onTileClick,
            unmountedFromLibraryIds,
            loadedModels,
        ],
    );

    return (
        <DirectoryUniformGrid
            productTiles={tiles}
            // The Display selection renders exactly as chosen — no sidebar
            // clamp. With the sidebar open the tiles just get narrower.
            columns={columns}
            mobileColumns={presentation.mobileColumns}
            gapVariant={presentation.gapVariant}
            skeletonAspectClass={presentation.skeletonAspectClass}
            entityNoun={entityNoun}
            pageSize={presentation.pageSize}
            imageSizes={presentation.imageSizes}
            totalCount={totalCount}
            hasMore={hasMore}
            isLoading={isLoading}
            onLoadMore={loadMore}
            hybridLoad={!presentation.autoLoadMore}
            autoLoadMore={presentation.autoLoadMore}
            nextPageHref={nextPageHref}
            // This instance owns ?page (its view-more hook has the
            // request-time rewrite off); tracking keeps the old write's
            // category identity (the category link, not the raw pathname).
            syncPageParam
            trackCategory={categoryLink}
            error={error}
            firstLoadedPage={currentFirstLoadedPage}
            requestedPage={requestedPage}
            onLoadSkeletonPage={loadSkeletonPage}
            loadingPages={loadingPages}
            injections={injections}
            activeCategoryLabel={activeCategoryLabel}
            featuredProjectIds={featuredProjectIds}
            removedFromLibraryIds={removedFromLibraryIds}
            onToggleFeature={onToggleFeature}
            onRemoveFromLibrary={onRemoveFromLibrary}
            contentGate={contentGate}
        />
    );
};
